import React, { useState } from 'react';
import { Paper, Typography, Box, Chip, IconButton, CircularProgress } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { IReference } from '../../../Types/Docs';
import { toast } from '../../../Utils/toast';

interface SourcesListProps {
  references: IReference[];
  'data-testid'?: string;
}

const SourcesList: React.FC<SourcesListProps> = ({
  references,
  'data-testid': testId,
}) => {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownload = async (docId: string, name: string) => {
    setDownloadingId(docId);
    try {
      const { downloadDocument } = await import('../../../Utils/docsApi');
      const blob = await downloadDocument(docId);

      if (!blob || blob.size === 0) {
        throw new Error('Received empty file');
      }

      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = name || 'document';
      window.document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        window.document.body.removeChild(a);
      }, 100);

      toast.success('Download started');
    } catch (error: unknown) {
      const err = error as { message?: string; response?: { data?: { message?: string } } };
      const errorMessage =
        err?.message || err?.response?.data?.message || 'Failed to download document';
      toast.error(errorMessage);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Paper sx={{ p: 3 }} data-testid={testId}>
      <Typography variant="h3" sx={{ mb: 2 }}>
        References ({references.length})
      </Typography>
      <Box display="flex" flexDirection="column" gap={2}>
        {references.map((ref, index) => (
          <Box
            key={ref.docId}
            sx={{
              p: 2,
              border: '1px solid #DEDEED',
              borderRadius: 1,
              '&:hover': { bgcolor: '#F8F8F8' },
            }}
            data-testid={`${testId}-reference-${index}`}
          >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box flex={1}>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {ref.name}
                </Typography>
                {ref.domain.length > 0 && (
                  <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                    {ref.domain.map((tag) => (
                      <Chip key={tag} label={tag} size="small" color="primary" variant="outlined" />
                    ))}
                  </Box>
                )}
                {ref.techStack.length > 0 && (
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {ref.techStack.map((tech) => (
                      <Chip key={tech} label={tech} size="small" variant="outlined" />
                    ))}
                  </Box>
                )}
              </Box>
              <IconButton
                onClick={() => handleDownload(ref.docId, ref.name)}
                disabled={downloadingId === ref.docId}
                aria-label={`Download ${ref.name}`}
                data-testid={`${testId}-download-${ref.docId}`}
                sx={{ ml: 2 }}
              >
                {downloadingId === ref.docId ? (
                  <CircularProgress size={20} />
                ) : (
                  <DownloadIcon />
                )}
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default SourcesList;
